import classNames from "classnames";
import {
	Address,
	Col,
	DefinitionItem,
	DefinitionList,
	EmptyState,
	LoadingState,
	PhoneNumber,
	Row,
	View,
} from "components/Shared";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import moment from "moment";
import React, { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * The tenants on this lease (GET /v2/tenants?lease_agreement_id=:id).
 *
 * Since the amendment ERD a Tenant carries a nullable `lease_agreement_id`
 * beside its existing FKs, so a Yardi lease's tenants are not reachable through
 * the `leases.tenant_id` path the property-level Tenants tab uses. Usually one
 * row; more than one means the payload's CustomerCode changed over the life of
 * the lease, and the newest is the lease's `current_tenant`.
 *
 * READ ONLY, with no way through to an edit form (Sim, 24 Aug). A Tenant is a
 * shared TCP record rather than part of this lease, and its Yardi identifiers
 * (CustomerCode / BrandCode) are what the importer dedupes on — so this tab
 * reports what Yardi says and nothing here offers to change it. `/tenants/:id/edit`
 * still exists and is still reachable from the project workspace; it is just not
 * offered from the lease.
 *
 * ---------------------------------------------------------------------------
 * ASSIGNMENTS: A TAG PER TENANT, AND THE ORDER IS THE SERVER'S (Sim, 4 Sep)
 * ---------------------------------------------------------------------------
 * A second tenant on a lease is an ASSIGNMENT — a payload naming a different
 * CustomerCode — and the two do not hold the lease at the same time. Each row
 * therefore carries `term_status`:
 *
 *   Current   holds the lease today.
 *   Future    an assignment that has not reached its AmendmentStartDate yet.
 *   Previous  the tenant an assignment replaced; its term ended the day the
 *             next one began.
 *
 * NOTHING HERE DERIVES IT. The dates are the amendments' own — a tenant starts
 * on the AmendmentStartDate of the amendment that introduced it and ends on the
 * day the next tenant starts — and LeaseAgreement::TenantTimeline works them
 * out, exactly as the Tenancies tab takes its row statuses from the fold. A
 * tenant introduced by a CANCELLED assignment is not in the response at all, so
 * the tenant before it is `current` again with no end date.
 *
 * THE RESPONSE IS ALREADY IN DISPLAY ORDER — current, then future, then
 * previous. `_.map` over it as it arrives; sorting here would be a second
 * opinion about the same question.
 *
 * THE TERM IS BESIDE THE TENANT, IN ITS OWN TABLE (Sim, 4 Sep) — which
 * amendment introduced this tenant, and the two dates the tag is derived from.
 * The same three columns the Tenancies tab gives a unit, and for the same
 * reason: "why does this row say Previous" has to be answerable on the row.
 *
 * A TABLE, not more DefinitionItems: the tenant's own fields on the left are
 * what YARDI says about the company, and the term on the right is what the
 * AMENDMENTS say about its tenure. Two different subjects, so they do not share
 * one list.
 */

interface Props {
	leaseAgreementId: string;
	name?: string;
	icon?: string;
}

/**
 * The same `badge-*` variants the lease's Tenancies tab uses for a unit's
 * status, so "current" means the same thing and looks the same on both tabs:
 * only the tenant holding the lease today gets the loud one.
 */
const STATUS_BADGES: Record<string, { label: string; className?: string }> = {
	current: { label: "tenant_status_current", className: "badge-primary" },
	future: { label: "tenant_status_future" },
	previous: { label: "tenant_status_previous" },
};

/**
 * Nothing at all for a tenant with no status — the property-level and
 * project-level tenant lists serialize the same record without a term, and an
 * empty badge beside a name reads as a missing value rather than a deliberate
 * blank.
 */
const StatusTag = ({ status }: { status?: string | null }) => {
	const badge = STATUS_BADGES[_.toString(status)];
	if (!badge) return null;

	return <span className={classNames("badge", badge.className)}>{translate(badge.label)}</span>;
};

// Same format as the lease's Details and Tenancies tabs, so a date means the
// same string wherever it is read.
const formatDate = (value?: string | null) => (!!value ? moment(value).format("D MMM YYYY") : null);

const dash = (value: any) => (value === 0 || !!value ? value : "—");

/**
 * The introducing amendment as a link to its own page, e.g. "Assignment
 * #10728835". Copied in behaviour from the Tenancies tab's own cell:
 *
 *   * the ROUTE takes the amendment's UUID, the LABEL is Yardi's AmendmentID
 *     string — never route on the one that is shown;
 *   * a NEW TAB (Sim, 25 Aug), because the list is what the reader is working
 *     through, with `rel` so the opened page gets no live `window.opener`;
 *   * an amendment with no UUID is shown muted rather than linked — a tenant
 *     imported before the amendment ERD has no introducer at all.
 */
const amendmentCell = (leaseAgreementId: string, amendment: any) => {
	const type = _.get(amendment, "type");
	const yardiId = _.get(amendment, "yardi_id");
	const id = _.get(amendment, "id");

	if (!type && !yardiId) return dash(null);

	return (
		<React.Fragment>
			{type || translate("amendment")}{" "}
			{!!yardiId &&
				(id ? (
					<Link to={`/lease-agreements/${leaseAgreementId}/amendments/${id}`} target="_blank" rel="noopener noreferrer">
						#{yardiId}
					</Link>
				) : (
					<span className="muted">#{yardiId}</span>
				))}
		</React.Fragment>
	);
};

/**
 * The tenant's term: who put it on the lease, and from when to when.
 *
 * ONE ROW, always rendered whole. An END DATE OF "—" IS THE ANSWER, not a
 * missing value — it means nothing has taken the lease off this tenant (Sim's
 * rule: the newest tenant has an infinite end date), so the cell shows the dash
 * rather than collapsing the column.
 *
 * Renders nothing at all only when the tenant has no term AND no introducer —
 * the property-level and project-level lists serialize the same record without
 * either, and an empty table there would be furniture around no content.
 */
const TermTable = ({ leaseAgreementId, tenant }: { leaseAgreementId: string; tenant: any }) => {
	const amendment = _.get(tenant, "term_amendment");
	const startDate = formatDate(_.get(tenant, "term_start_date"));
	const endDate = formatDate(_.get(tenant, "term_end_date"));

	if (!amendment && !startDate && !endDate) return null;

	return (
		<table className="table" style={{ width: "100%" }}>
			<thead>
				<tr>
					<th>{translate("amendment_introduced_by")}</th>
					<th>{translate("start_date")}</th>
					<th>{translate("end_date")}</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>{amendmentCell(leaseAgreementId, amendment)}</td>
					<td style={{ whiteSpace: "nowrap" }}>{dash(startDate)}</td>
					<td style={{ whiteSpace: "nowrap" }}>{dash(endDate)}</td>
				</tr>
			</tbody>
		</table>
	);
};

const TenantsTab = ({ leaseAgreementId }: Props) => {
	// null = no response yet — see the note in ContactsTab.
	const [tenants, setTenants] = useState<any[] | null>(null);
	const [datasource] = useState(new Datasource({ mainModelName: "tenant" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setTenants(response.normalizedMainModelResponse || [])
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (!_.isEmpty(leaseAgreementId))
			datasource.get("v2", "tenants", {
				queryStringParameters: { lease_agreement_id: leaseAgreementId, per_page: 1000 },
			});
	}, [datasource, leaseAgreementId]);

	if (tenants === null) return <LoadingState />;

	// The shared empty state, so a lease with no tenants reads like every other
	// empty list in the app rather than like a stray line of text (Sim, 24 Aug).
	if (_.isEmpty(tenants))
		return (
			<View>
				<EmptyState resource="tenants" />
			</View>
		);

	return (
		<View>
			{_.map(tenants, (tenant, index) => (
				<React.Fragment key={tenant.id}>
					{index > 0 && <hr style={{ margin: "1.5rem 0" }} />}
					{/* `badges` puts the tag inside the card's own <h2>, beside the
					    name — the shared slot for exactly this, so the heading keeps
					    its markup and the tag needs no styling of its own. */}
					<View title={_.get(tenant, "name")} badges={<StatusTag status={_.get(tenant, "term_status")} />}>
						{/* The shared grid, so the two halves line up with every other
						    two-column block in the app and collapse the same way on a
						    narrow screen. */}
						<Row>
							<Col>
								<DefinitionList>
									<DefinitionItem title="trading_name" value={_.get(tenant, "trading_name")} />
									{/* Yardi identifiers — CustomerCode/CustomerName/BrandCode/
									    BrandName. Deliberately not masked while a project is
									    pending, unlike the contact fields: these are system
									    identifiers, and they are what an administrator
									    reconciles a pending import against. */}
									<DefinitionItem title="customer_code" value={_.get(tenant, "customer_code")} />
									<DefinitionItem title="customer_name" value={_.get(tenant, "customer_name")} />
									<DefinitionItem title="brand_code" value={_.get(tenant, "brand_code")} />
									<DefinitionItem title="brand_name" value={_.get(tenant, "brand_name")} />
									<DefinitionItem title="contact_name" value={_.get(tenant, "contact_name")} />
									<DefinitionItem title="contact_email" value={_.get(tenant, "contact_email")} />
									<DefinitionItem
										title="phone_number"
										value={<PhoneNumber phone_number={_.get(tenant, "phone_number")} />}
									/>
									{!!_.get(tenant, "addresses", []).length && (
										<DefinitionItem title="address">
											{_.map(_.get(tenant, "addresses"), (address: any) => (
												<Address key={address.id} address={address} />
											))}
										</DefinitionItem>
									)}
								</DefinitionList>
							</Col>
							<Col>
								<TermTable leaseAgreementId={leaseAgreementId} tenant={tenant} />
							</Col>
						</Row>
					</View>
				</React.Fragment>
			))}
		</View>
	);
};

export default memo(TenantsTab);
